import { FolderType, IDefaultFolderDto } from '../config/documentConfig'

/**
 * A chain only counts as enabled when every segment is enabled: a folder below a disabled
 * parent is never created either.
 */
export const isEnabledDefaultFolderPath = (path: IDefaultFolderDto[]): boolean => !path.find(folder => folder.enabled === false)

/**
 * Find every chain (root to leaf) to a default folder with the given type, depth-first. Fully
 * enabled chains come first, so callers can prefer the current structure over disabled
 * leftovers that only exist in dossiers created before a structure change.
 */
export const findDefaultFolderPaths = (
    defaultFolders: IDefaultFolderDto[],
    folderType: FolderType | string,
): IDefaultFolderDto[][] => {
    const collect = (folders: IDefaultFolderDto[], ancestors: IDefaultFolderDto[]): IDefaultFolderDto[][] =>
        (folders || []).flatMap(folder => {
            const path = [...ancestors, folder]
            return [...(folder.type === folderType ? [path] : []), ...collect(folder.children, path)]
        })

    const paths = collect(defaultFolders, [])
    return [...paths.filter(isEnabledDefaultFolderPath), ...paths.filter(path => !isEnabledDefaultFolderPath(path))]
}

/**
 * Find the chain (root to leaf) to the first default folder with the given type: the first
 * enabled chain, or the first chain overall when no enabled chain exists.
 */
export const findDefaultFolderPath = (
    defaultFolders: IDefaultFolderDto[],
    folderType: FolderType | string,
): IDefaultFolderDto[] => findDefaultFolderPaths(defaultFolders, folderType)[0]

/**
 * Flatten the (possibly nested) default folders in pre-order.
 */
export const flattenDefaultFolders = (defaultFolders: IDefaultFolderDto[]): IDefaultFolderDto[] =>
    (defaultFolders || []).flatMap(defaultFolder => [defaultFolder, ...flattenDefaultFolders(defaultFolder.children)])
